/* eslint-disable @atlassian/relay/graphql-naming */
import React, {
	Fragment,
	memo,
	useCallback,
	useMemo,
	useState,
	type CSSProperties,
	type ReactNode,
} from 'react';
import castArray from 'lodash/castArray';
import noop from 'lodash/noop';
import memoize from 'lodash/memoize';
import { graphql, useFragment } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { Box, xcss, type XCSS } from '@atlaskit/primitives';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fg } from '@atlassian/jira-feature-gating';
import type { TriggerPropsType } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import {
	KNOWN_COLUMN_TYPES,
	COLUMN_ID_MERGED_COLUMN,
} from '@atlassian/jira-issue-table-common/src/constants.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import type { StatusUpdateHandler } from '@atlassian/jira-issue-table-hide-done-toggle/src/controllers/handle-status-update/types.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useScrollStateSelector } from '../../../controllers/scroll-state/index.tsx';
import {
	COLUMN_ID_MEATBALL_MENU,
	COLUMN_ID_EXPAND_BUTTON,
	COLUMN_ID_CHECKBOX,
	PACKAGE_NAME,
	TEAM_NAME,
} from '../../../common/constants.tsx';
import type { Column } from '../../../common/types.tsx';
import { AffectedServicesCell } from '../../../common/ui/issue-cells/affected-services/AffectedServicesCell.tsx';
import { AtlasProjectCell } from '../../../common/ui/issue-cells/atlas-project/AtlasProjectCell.tsx';
import { LoadingCell } from '../../../common/ui/issue-cells/loading-cell/index.tsx';
import { CheckboxCell } from '../../../common/ui/issue-cells/checkbox/index.tsx';
import { CheckboxSelectCell } from '../../../common/ui/issue-cells/checkbox-select/CheckboxSelectCell.tsx';
import { CmdbObjectsCell } from '../../../common/ui/issue-cells/cmdb-objects/CmdbObjectsCell.tsx';
import { ComponentsInlineEditCell } from '../../../common/ui/issue-cells/components/ComponentsCell.tsx';
import { CascadingSelectCell } from '../../../common/ui/issue-cells/cascading-select/CascadingSelectCell.tsx';
import {
	DateCell,
	DateInlineEditCell,
} from '../../../common/ui/issue-cells/date/DateInlineEditCell.tsx';
import { DateTimeCell } from '../../../common/ui/issue-cells/date-time/DateTimeCell.tsx';
import { EpicLinkCell } from '../../../common/ui/issue-cells/epic-link/EpicLinkCell.tsx';
import { FallbackCell } from '../../../common/ui/issue-cells/fallback/FallbackCell.tsx';
import { FlagCell } from '../../../common/ui/issue-cells/flag/FlagCell.tsx';
import { GoalsCell } from '../../../common/ui/issue-cells/goals/GoalsCell.tsx';
import { IssueKeyCell } from '../../../common/ui/issue-cells/issue-key/IssueKeyCell.tsx';
import { IssueLinksCell } from '../../../common/ui/issue-cells/issue-links/IssueLinksCell.tsx';
import { IssueOperations } from '../../../common/ui/issue-cells/issue-operations/index.tsx';
import { IssueStatusCategoryCell } from '../../../common/ui/issue-cells/issue-status-category/IssueStatusCategoryCell.tsx';
import { IssueStatusCell } from '../../../common/ui/issue-cells/issue-status/IssueStatusCell.tsx';
import { IssueSummaryCell } from '../../../common/ui/issue-cells/issue-summary/IssueSummaryCell.tsx';
import { IssueTypeCell } from '../../../common/ui/issue-cells/issue-type/IssueTypeCell.tsx';
import { LabelsCell } from '../../../common/ui/issue-cells/labels/LabelsCell.tsx';
import { MultiSelectCell } from '../../../common/ui/issue-cells/multi-select/MultiSelectCell.tsx';
import { MultiUserPickerCell } from '../../../common/ui/issue-cells/multi-user-picker/MultiUserPickerCell.tsx';
import { ConfluencePagesCell } from '../../../common/ui/issue-cells/confluence-pages/ConfluencePagesCell.tsx';
import { NumberCell } from '../../../common/ui/issue-cells/number/NumberCell.tsx';
import { OriginalEstimateCell } from '../../../common/ui/issue-cells/original-estimate/OriginalEstimateCell.tsx';
import { ParentCell } from '../../../common/ui/issue-cells/parent/ParentCell.tsx';
import { PlainTextCell } from '../../../common/ui/issue-cells/plain-text/PlainTextCellOld.tsx';
import { PriorityCell } from '../../../common/ui/issue-cells/priority/PriorityCell.tsx';
import { ProjectCell } from '../../../common/ui/issue-cells/project/ProjectCell.tsx';
import { RadioSelectCell } from '../../../common/ui/issue-cells/radio-select/RadioSelectCell.tsx';
import { RequestTypeCell } from '../../../common/ui/issue-cells/request-type/RequestTypeCell.tsx';
import { ResolutionCell } from '../../../common/ui/issue-cells/resolution/ResolutionCell.tsx';
import { RichTextCell } from '../../../common/ui/issue-cells/rich-text/RichTextCell.tsx';
import { SingleSelectCell } from '../../../common/ui/issue-cells/single-select/SingleSelectCell.tsx';
import { SprintCell } from '../../../common/ui/issue-cells/sprint/SprintCell.tsx';
import { TeamCell } from '../../../common/ui/issue-cells/team/TeamCell.tsx';
import { UserCell } from '../../../common/ui/issue-cells/user/UserCellInternal.tsx';
import { TableCell, type Props as TableCellProps } from '../../../common/ui/table-cell/index.tsx';
import { getProjectKeyFromIssueKey, isNonNullish } from '../../../common/utils/index.tsx';
import {
	useIsConditionalFormattingEnabled,
	useIsFixedTableLayoutEnabled,
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsIssueHierarchyEnabled,
	useIsIssueOperationMeatballMenuEnabled,
	useIsMergedColumnEnabled,
	useIsTimelineEnabled,
} from '../../../controllers/features/selectors.tsx';
import { useRowContext } from '../../rows/row-context/index.tsx';
import { CommentSummaryCell } from '../../../common/ui/issue-cells/comment-summary/CommentSummaryCell.tsx';
import { UrlCell } from '../../../common/ui/issue-cells/url/UrlCell.tsx';
import { MultiVersionPickerCell } from '../../../common/ui/issue-cells/multi-version-picker/MultiVersionPickerCell.tsx';
import { useHideDoneItems } from '../../../controllers/hide-done-items/index.tsx';
import type { JiraStatusField } from '../../../common/ui/issue-cells/issue-status/utils.tsx';
import { MultiGroupPickerCell } from '../../../common/ui/issue-cells/multi-group-picker/MultiGroupPickerCell.tsx';
import { TimelineCellRenderer } from '../../../common/ui/issue-cells/timeline/TimelineCellRenderer.tsx';
import { WatchersCell } from '../../../common/ui/issue-cells/watches/WatchersCell.tsx';
import { LinkRelationshipTypeCell } from '../../../common/ui/issue-cells/link-relationship-type/LinkRelationshipTypeCell.tsx';
import { useCellsConditionalFormatting } from '../hooks/useCellsConditionalFormatting.tsx';
import type { IssueRow_rules$key } from './__generated__/IssueRow_rules.graphql';
import type { IssueRow$key } from './__generated__/IssueRow.graphql';
import type { IssueRowWithFragments_issueEdge$key } from './__generated__/IssueRowWithFragments_issueEdge.graphql';
import type { IssueRow_fieldSets$key } from './__generated__/IssueRow_fieldSets.graphql';
import IssueRowCell from './cell/index.tsx';
import FirstColumnCell from './first-column-cell/FirstColumnCell.tsx';
import { MergedCell } from './merged-cell/MergedCell.tsx';
import { getMergedFields } from './merged-cell/utils.tsx';

export type Props = {
	project?: IssueRow$key | null;
	issueEdge: IssueRowWithFragments_issueEdge$key;
	fieldSets: IssueRow_fieldSets$key | null;
	formattingRules?: IssueRow_rules$key | null;
	cellBorder?: TableCellProps['border'];
	/**
	 * Element to render within the first `td` of the issue row. This is to enable rendering of absolute/fixed position
	 * elements relative to the start of the table row while retaining semantically valid HTML.
	 */
	beforeFirstCell?: ReactNode;
	itemsConnectionId: string;
	depth: number;
	isRowHoveredOrSelected?: boolean;
	rowIndex: number;
	triggerProps?: TriggerPropsType;
	isFormVisible?: boolean;
	isStickyColumnsVisible?: boolean;
};

/**
 * We memoize this event by the **first** argument, e.g. typename, as we only really care about the proportion of
 * users encountering this scenario. We don't need to send an event each time a cell is rendered.
 */
export const fireErrorAnalyticsOncePerGraphQLType: (
	typename: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => void = memoize((typename: string, createAnalyticsEvent: CreateUIAnalyticsEvent) => {
	fireErrorAnalytics({
		meta: {
			id: 'tableCellUnhandledGraphQLType',
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
		},
		error: new Error(`Unhandled GraphQL type ${typename} when trying to render issue field.`),
		event: createAnalyticsEvent({}),
		attributes: {
			typename,
		},
	});
});

export const IssueRowWithFragments = ({
	project,
	issueEdge,
	fieldSets,
	cellBorder,
	beforeFirstCell,
	itemsConnectionId,
	depth,
	isRowHoveredOrSelected,
	rowIndex,
	triggerProps,
	isFormVisible,
	isStickyColumnsVisible,
	formattingRules,
}: Props): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isTimelineEnabled = useIsTimelineEnabled();

	// State for managing merged cell focus
	const [isMergedCellFocused, setIsMergedCellFocused] = useState(false);

	const projectRef = useFragment(
		graphql`
			fragment IssueRow on JiraProject
			@argumentDefinitions(
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isSavedViewConditionalFormattingEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-conditional-formatting-enabled.relayprovider"
				}
			) {
				...useCellsConditionalFormatting_rules
					@include(if: $isConditionalFormattingEnabled)
					@skip(if: $isSavedViewConditionalFormattingEnabled)
			}
		`,
		project,
	);

	const formattingRulesRef = useFragment(
		graphql`
			fragment IssueRow_rules on JiraFormattingRuleConnection
			@argumentDefinitions(
				isSavedViewConditionalFormattingEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-conditional-formatting-enabled.relayprovider"
				}
			) {
				...useCellsConditionalFormatting_savedView_rules
					@include(if: $isSavedViewConditionalFormattingEnabled)
			}
		`,
		formattingRules,
	);

	const issueEdgeData = useFragment<IssueRowWithFragments_issueEdge$key>(
		graphql`
			fragment IssueRowWithFragments_issueEdge on JiraIssueEdge
			@argumentDefinitions(
				isJscMergedColumnEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsc-merged-column.relayprovider"
				}
				isJiraItrM4Exp3Enabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-jira-itr-m4-exp-3-enabled.relayprovider"
				}
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
			) {
				...FirstColumnCell
					@arguments(
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
					)
				...MergedCell
					@include(if: $isJscMergedColumnEnabled)
					@arguments(
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
					)
				node @required(action: THROW) {
					__id
					key
					issueId
					issueTypeField {
						issueType {
							hierarchy {
								level
							}
						}
					}
					issueTypeField @include(if: $isJiraItrM4Exp3Enabled) {
						issueType {
							name
						}
					}
					...IssueSummaryCell_issue
						@arguments(isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled)
					...IssueKeyCell_fields
						@arguments(isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled)
					...RichTextCell_issue @arguments(isDensityFull: $isDensityFull)
					...LinkRelationshipTypeCell_issueRef @include(if: $isLinkRelationshipFieldEnabled)
				}
			}
		`,
		issueEdge,
	);
	const issueData = issueEdgeData?.node;
	const fieldSetsData = useFragment<IssueRow_fieldSets$key>(
		graphql`
			fragment IssueRow_fieldSets on JiraIssueFieldSetConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isAtlasProjectEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/atlas-project-native-integration.relayprovider"
				}
				originalEstimateEditableFixFlag: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-original-estimate-editable-fix.relayprovider"
				}
			) {
				...useCellsConditionalFormatting @include(if: $isConditionalFormattingEnabled)
				edges {
					node {
						type
						fieldSetId
						fields {
							... on JiraIssueFieldConnection {
								totalCount
								edges {
									node {
										id
										__id
										__typename
										fieldId
										type
										...AffectedServicesCell
										...CascadingSelectCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...CmdbObjectsCell
										...ComponentsCell_ComponentsInlineEditCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...DateInlineEditCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...DateInlineEditCell_DateInlineEditCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...DateTimeCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...EpicLinkCell
										...FallbackCell
										...IssueKeyCell
										...IssueStatusCategoryCell
										...IssueStatusCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...IssueSummaryCell
											@arguments(
												isInlineEditingEnabled: $isInlineEditingEnabled
												isEditableSyncLoaded: $isEditableSyncLoaded
											)
										...LabelsCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...IssueTypeCell
										...ParentCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...PriorityCell
											@arguments(
												isInlineEditingEnabled: $isInlineEditingEnabled
												isEditableSyncLoaded: $isEditableSyncLoaded
											)
										...ProjectCell
										...ResolutionCell
										...SprintCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...UserCellInternal
											@arguments(
												isInlineEditingEnabled: $isInlineEditingEnabled
												isEditableSyncLoaded: $isEditableSyncLoaded
											)
										...PlainTextCellOld @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...RichTextCell @arguments(isDensityFull: $isDensityFull)
										...RequestTypeCell
										...IssueLinksCell
										...NumberCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...CommentSummaryCell
										...UrlCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...SingleSelectCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...MultiSelectCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...CheckboxSelectCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...MultiUserPickerCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...TeamCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...OriginalEstimateCell
											@skip(if: $originalEstimateEditableFixFlag)
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...OriginalEstimateCell_OriginalEstimateCellNew
											@include(if: $originalEstimateEditableFixFlag)
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...RadioSelectCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...MultiVersionPickerCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...GoalsCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...AtlasProjectCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
											@include(if: $isAtlasProjectEnabled)
										...ConfluencePagesCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...MultiGroupPickerCell
											@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
										...FlagCell
										...WatchersCell
										...LinkRelationshipTypeCell_fieldRef
											@include(if: $isLinkRelationshipFieldEnabled)
											@arguments(
												isInlineEditingEnabled: $isInlineEditingEnabled
												isEditableSyncLoaded: $isEditableSyncLoaded
											)
										...TimelineCellRenderer @arguments(isTimelineEnabled: $isTimelineEnabled)
									}
								}
							}
						}
					}
				}
			}
		`,
		fieldSets,
	);

	// eslint-disable-next-line no-nested-ternary
	const colorForId = fg('jira_list_conditional_formatting')
		? fg('saved_list_view_technical_changes')
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useCellsConditionalFormatting(null, fieldSetsData, formattingRulesRef)
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useCellsConditionalFormatting(projectRef, fieldSetsData)
		: {}; // placeholder for what will soon be const color = ...
	const isCreateSubTaskAllowed =
		issueData && issueData.issueTypeField?.issueType?.hierarchy?.level === 0;

	const { columns, tableInjectedProps, groupConnectionId, groupId, resizingColumnIndex } =
		useRowContext();

	const columnConfigByIdMap = useMemo(() => {
		const result = new Map<string | null, Column>();
		columns.forEach((columnConfig) => {
			result.set(columnConfig.id, columnConfig);
		});
		return result;
	}, [columns]);

	const onFocusHandler = useCallback(() => setIsMergedCellFocused(true), []);
	const onBlurHandler = useCallback((e: React.FocusEvent) => {
		const currentTarget = e.currentTarget;
		const relatedTarget = e.relatedTarget;

		// Check if focus is staying within the merged cell
		if (relatedTarget && currentTarget.contains(relatedTarget)) {
			return;
		}

		// Check if this cell has a menu trigger (for portal-rendered menus)
		const menuTriggerInCell = currentTarget.querySelector(
			'[aria-haspopup="true"], [aria-haspopup="menu"]',
		);

		if (menuTriggerInCell) {
			// Check if the menu is currently open
			if (menuTriggerInCell.getAttribute('aria-expanded') === 'true') {
				return;
			}

			// Check if focus moved to the menu/menuitem in a portal
			if (relatedTarget instanceof HTMLElement) {
				const isMenuOrMenuItem =
					relatedTarget.closest('[role="menu"], [role="menuitem"]') ||
					relatedTarget.querySelector('[role="menu"], [role="menuitem"]');

				if (isMenuOrMenuItem) {
					return;
				}
			}
		}

		// Focus has left the merged cell system entirely
		setIsMergedCellFocused(false);
	}, []);

	const isIssueHierarchyEnabled = useIsIssueHierarchyEnabled();

	let handleStatusUpdate: StatusUpdateHandler = noop;
	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('jsc_list_hide_done_items') && !fg('memoise_issuetable_components')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		handleStatusUpdate = useHideDoneItems({
			itemsConnectionId,
			recordId: issueData?.__id,
			projectKey: issueData?.key && getProjectKeyFromIssueKey(issueData.key),
			groupConnectionId,
		});
	}

	let isMergedColumnEnabled = false;
	if (fg('jira_list_merged_column_gate')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isMergedColumnEnabled = useIsMergedColumnEnabled();
	}

	const handleUpdateCacheNew = useCallback<
		(issueKey: string, fieldName: string, field: JiraStatusField) => void
	>(
		(issueKey, fieldName, field) => {
			tableInjectedProps.onUpdateCache?.(issueKey, fieldName, field);
			handleStatusUpdate(field);
		},
		[handleStatusUpdate, tableInjectedProps],
	);

	const handleUpdateCache =
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jsc_list_hide_done_items') && !fg('memoise_issuetable_components')
			? handleUpdateCacheNew
			: tableInjectedProps.onUpdateCache;

	const isIssueOperationMeatballMenuEnabled = useIsIssueOperationMeatballMenuEnabled();
	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();

	const fieldElementByIdMap = useMemo(() => {
		// Map all issue fields into their respective cell type keyed by fieldSetId. This will be used as a lookup when
		// mapping table columns in the row.
		const result = new Map<
			string,
			{
				cellElement: ReactNode;
				spacing?: XCSS;
				multipleFieldLayoutAdjustment?: boolean;
				columnIds?: string[];
				fieldId?: string;
			}
		>();

		if (
			isIssueHierarchyEnabled &&
			issueEdgeData &&
			!(isMergedColumnEnabled && fg('jira_list_merged_column_gate'))
		) {
			const element = (
				<FirstColumnCell
					isRowHoveredOrSelected={Boolean(isRowHoveredOrSelected)}
					itemsConnectionId={itemsConnectionId}
					depth={depth}
					issueRef={issueEdgeData}
					triggerProps={triggerProps}
					isFormVisible={isFormVisible}
				/>
			);
			result.set(COLUMN_ID_EXPAND_BUTTON, {
				cellElement: element,
				spacing: noSpacingStyles,
			});
		}

		if (issueEdgeData && isMergedColumnEnabled && fg('jira_list_merged_column_gate')) {
			const { issueTypeField, issueKeyField, summaryField, fieldId } =
				getMergedFields(fieldSetsData);
			const isHierarchyEnabled = isIssueHierarchyEnabled;

			if (isHierarchyEnabled || issueTypeField || issueKeyField || summaryField) {
				const mergedColumns =
					columns
						.find(({ id }) => id === COLUMN_ID_MERGED_COLUMN)
						?.mergedColumns?.map(({ id }) => id) ?? [];

				const element = (
					<MergedCell
						key={COLUMN_ID_MERGED_COLUMN}
						itemsConnectionId={itemsConnectionId}
						depth={depth}
						issueRef={issueEdgeData}
						isRowHoveredOrSelected={Boolean(isRowHoveredOrSelected)}
						isCreateChildFormVisible={isFormVisible}
						createChildTriggerProps={triggerProps}
						groupId={groupId}
						isHierarchyEnabled={isHierarchyEnabled}
						{...(fg('jira_list_merged_cell_action_buttons_visibility') && {
							isFocused: isMergedCellFocused,
						})}
						renderContent={({ actionButtons, isMergedCellFocusedOrHovered }) => (
							<>
								{issueTypeField && mergedColumns.includes('issuetype') && (
									<Box xcss={isHierarchyEnabled ? typeCellStyles : typeCellNoHierarchyStyles}>
										<IssueTypeCell fieldRef={issueTypeField} />
									</Box>
								)}
								{issueKeyField && mergedColumns.includes('issuekey') && (
									<IssueKeyCell
										fieldRef={issueKeyField}
										issueRef={issueData}
										onIssueLinkSelected={tableInjectedProps.onIssueLinkSelected}
										isRowHoveredOrSelected={
											fg('jsc-2733-copy_link_from_issue_key')
												? Boolean(isRowHoveredOrSelected)
												: undefined
										}
										isTruncated={Boolean(summaryField) === false}
									/>
								)}
								{!summaryField && <Box xcss={childCreateButtonStyle}>{actionButtons}</Box>}
								{summaryField && mergedColumns.includes('summary') && (
									<IssueSummaryCell
										fieldRef={summaryField}
										issueRef={issueData}
										onIssueLinkSelected={tableInjectedProps.onIssueLinkSelected}
										isRowHoveredOrSelected={Boolean(isRowHoveredOrSelected)}
										readViewHighlightOnHover={false}
										{...(fg('jira_list_merged_cell_action_buttons_visibility') && {
											isMergedCellFocusedOrHovered,
										})}
									>
										{actionButtons}
									</IssueSummaryCell>
								)}
							</>
						)}
					/>
				);

				result.set(COLUMN_ID_MERGED_COLUMN, {
					cellElement: element,
					spacing: noSpacingStyles,
					multipleFieldLayoutAdjustment: true,
					columnIds: mergedColumns,
					...(fieldId ? { fieldId } : {}),
				});
			}
		}

		fieldSetsData?.edges
			?.map((edge) => edge?.node)
			.filter(isNonNullish)
			.forEach((fieldSet) => {
				// @ts-expect-error - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string | null'.
				const columnConfig = columnConfigByIdMap.get(fieldSet.fieldSetId);
				if (!fieldSet.fieldSetId || !columnConfig) {
					return;
				}

				const projectKey = getProjectKeyFromIssueKey(issueData.key);

				// Handle Collapsed Fields on a single issue (rare) by simply rendering a node for each field.
				const fieldElements: ReactNode[] = [];
				// Allow td spacing to be configured for the given field type
				let spacing: XCSS | undefined;
				// Stop gap fix to wrap inline editable field elements in styled containers when there are multiple
				// fields within a single cell. This is necessary as inline editable fields have min-height: 100% by
				// default.
				// TODO JSC-1783 This approach will need to be revised as part of single row height once we gather data
				//  on prevalence of this edge case.
				let multipleFieldLayoutAdjustment: boolean | undefined;
				let fieldId: string | undefined;

				const hasDuplicates = (fieldSet.fields?.totalCount ?? 1) > 1;

				fieldSet.fields?.edges?.forEach((fieldEdge, count) => {
					// True if it is the first occuring cell
					const isFirstField = count === 0;
					fieldId = fieldEdge?.node?.id;

					switch (fieldEdge?.node?.__typename) {
						case 'JiraAffectedServicesField':
							fieldElements.push(
								<AffectedServicesCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraCascadingSelectField':
							fieldElements.push(
								<CascadingSelectCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraCMDBField':
							fieldElements.push(
								<CmdbObjectsCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);

							break;
						case 'JiraCheckboxesField':
							fieldElements.push(
								<CheckboxSelectCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraComponentsField':
							spacing = noSpacingStyles;
							fieldElements.push(
								<ComponentsInlineEditCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueSearchBaseUrl={tableInjectedProps.issueSearchBaseUrl}
									jqlTerm={columnConfig.jqlTerm}
									projectKey={projectKey}
								/>,
							);
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraTimelineField':
							if (isTimelineEnabled) {
								fieldElements.push(
									<TimelineCellRenderer
										key={fieldEdge.node.fieldId}
										depth={depth}
										groupId={groupId}
										fieldRef={fieldEdge.node}
									/>,
								);
								spacing = noSpacingStyles;
							}
							break;
						case 'JiraAggregatedTimelineField':
							if (isTimelineEnabled && fg('jira_timeline_rollups_technical_gate')) {
								fieldElements.push(
									<TimelineCellRenderer
										key={fieldEdge.node.fieldId}
										depth={depth}
										groupId={groupId}
										fieldRef={fieldEdge.node}
									/>,
								);
								spacing = noSpacingStyles;
							}
							break;
						case 'JiraDatePickerField':
							if (isInlineEditingExtendedFieldSupportEnabled) {
								fieldElements.push(
									<DateInlineEditCell
										key={fieldEdge.node.fieldId}
										fieldRef={fieldEdge.node}
										isFirstField={isFirstField}
										hasDuplicates={hasDuplicates}
									/>,
								);
								spacing = noSpacingStyles;
								multipleFieldLayoutAdjustment = true;
							} else {
								fieldElements.push(
									<DateCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
								);
								if (fieldEdge.node.fieldId === KNOWN_COLUMN_TYPES.DUE_DATE) {
									spacing = noSpacingStyles;
								}
							}
							break;
						case 'JiraDateTimePickerField':
							fieldElements.push(
								<DateTimeCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraEpicLinkField':
							// This field is deprecated; this field will show text "Replaced by Parent field" as content
							fieldElements.push(
								<EpicLinkCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraFallbackField':
							fieldElements.push(
								<FallbackCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							spacing = noSpacingStyles;
							break;
						case 'JiraIssueTypeField':
							fieldElements.push(
								<IssueTypeCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraLabelsField':
							fieldElements.push(
								<LabelsCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueSearchBaseUrl={tableInjectedProps.issueSearchBaseUrl}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraMultipleSelectField':
							fieldElements.push(
								<MultiSelectCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);

							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraMultipleSelectUserPickerField':
							// This field is not treated with single line row height since the read view
							// is using <AvatarGroup> component from @atlaskit/avatar-group. Until this component is modified,
							// or rebuilt, right truncation will not be applied.
							fieldElements.push(
								<MultiUserPickerCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);

							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraMultipleGroupPickerField':
							fieldElements.push(
								<MultiGroupPickerCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							break;
						case 'JiraNumberField':
							fieldElements.push(
								<NumberCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);

							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraParentIssueField':
							fieldElements.push(
								<ParentCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									fieldType={fieldEdge.node.type}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraPriorityField':
							fieldElements.push(
								<PriorityCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);

							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraProjectField':
							fieldElements.push(
								<ProjectCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isRequired={fieldEdge.node.type === KNOWN_COLUMN_TYPES.PROJECT}
								/>,
							);
							break;
						case 'JiraResolutionField':
							fieldElements.push(
								<ResolutionCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraRichTextField':
							fieldElements.push(
								<RichTextCell
									issueKey={issueData.key}
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueRef={issueData}
									isFirstField={isFirstField}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraSingleLineTextField':
							if (fieldEdge.node.type === KNOWN_COLUMN_TYPES.ISSUE_KEY) {
								fieldElements.push(
									<IssueKeyCell
										key={fieldEdge.node.fieldId}
										fieldRef={fieldEdge.node}
										issueRef={issueData}
										isRowHoveredOrSelected={
											fg('jsc-2733-copy_link_from_issue_key')
												? Boolean(isRowHoveredOrSelected)
												: undefined
										}
										onIssueLinkSelected={tableInjectedProps.onIssueLinkSelected}
									/>,
								);
								spacing = noSpacingStyles;
							} else if (fieldEdge.node.type === KNOWN_COLUMN_TYPES.SUMMARY) {
								fieldElements.push(
									<IssueSummaryCell
										key={fieldEdge.node.fieldId}
										fieldRef={fieldEdge.node}
										issueRef={issueData}
										onIssueLinkSelected={tableInjectedProps.onIssueLinkSelected}
									/>,
								);

								spacing = noSpacingStyles;
								multipleFieldLayoutAdjustment = true;
							} else {
								fieldElements.push(
									<PlainTextCell
										key={fieldEdge.node.fieldId}
										fieldRef={fieldEdge.node}
										isFirstField={isFirstField}
										hasDuplicates={hasDuplicates}
									/>,
								);
								multipleFieldLayoutAdjustment = true;
								spacing = noSpacingStyles;
							}
							break;
						case 'JiraSingleSelectUserPickerField':
							fieldElements.push(
								<UserCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									projectKey={projectKey}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
									{...(fg('jira_timeline_sticky_assignee_status_columns') && {
										isColumnSticky: columnConfig?.isColumnSticky,
									})}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraSingleSelectField':
							fieldElements.push(
								<SingleSelectCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraSprintField':
							fieldElements.push(
								<SprintCell
									key={fieldEdge.node.fieldId}
									projectKey={projectKey}
									fieldRef={fieldEdge.node}
									issueSearchBaseUrl={tableInjectedProps.issueSearchBaseUrl}
									jqlTerm={columnConfig.jqlTerm}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraStatusCategoryField':
							fieldElements.push(
								<IssueStatusCategoryCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraStatusField':
							fieldElements.push(
								<IssueStatusCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueKey={issueData.key}
									issueId={issueData.issueId}
									onUpdateCache={handleUpdateCache}
									recordId={issueData?.__id}
									itemsConnectionId={itemsConnectionId}
									groupConnectionId={groupConnectionId}
									{...(fg('jira_timeline_sticky_assignee_status_columns') && {
										isColumnSticky: columnConfig?.isColumnSticky,
									})}
								/>,
							);
							break;
						case 'JiraServiceManagementRequestTypeField':
							fieldElements.push(
								<RequestTypeCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraTeamViewField':
							fieldElements.push(
								<TeamCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraIssueLinkField':
							fieldElements.push(
								<IssueLinksCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueSearchBaseUrl={tableInjectedProps.issueSearchBaseUrl}
									issueKey={issueData.key}
								/>,
							);
							break;
						case 'JiraCommentSummaryField':
							fieldElements.push(
								<CommentSummaryCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueKey={issueData.key}
									onIssueLinkSelected={tableInjectedProps.onIssueLinkSelected}
								/>,
							);
							spacing = noSpacingStyles;
							break;
						case 'JiraUrlField':
							fieldElements.push(
								<UrlCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraOriginalTimeEstimateField':
							fieldElements.push(
								<OriginalEstimateCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraRadioSelectField':
							fieldElements.push(
								<RadioSelectCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraMultipleVersionPickerField':
							fieldElements.push(
								<MultiVersionPickerCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isFirstField={isFirstField}
									hasDuplicates={hasDuplicates}
								/>,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraGoalsField':
							fieldElements.push(
								<GoalsCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraTownsquareProjectField':
							if (
								expValEquals(
									'atlassian_projects_-_native_integration',
									'cohort',
									'variation',
									'control',
								)
							) {
								fieldElements.push(
									<AtlasProjectCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
								);
								spacing = noSpacingStyles;
								multipleFieldLayoutAdjustment = true;
							}
							break;
						case 'JiraConfluenceRemoteIssueLinksField':
							fieldElements.push(
								<ConfluencePagesCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							spacing = noSpacingStyles;
							multipleFieldLayoutAdjustment = true;
							break;
						case 'JiraFlagField':
							fieldElements.push(
								<FlagCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraWatchesField':
							fieldElements.push(
								<WatchersCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;

						case 'JiraIssueLinkRelationshipTypeField':
							if (fg('jira-linked-work-items-targeting-gate')) {
								fieldElements.push(
									<LinkRelationshipTypeCell
										key={fieldEdge.node.fieldId}
										issueRef={issueData}
										fieldRef={fieldEdge.node}
										onIssueUnlink={tableInjectedProps.onIssueDelete}
										issueStoreId={issueData?.__id}
										itemsConnectionId={itemsConnectionId}
									/>,
								);

								spacing = noSpacingStyles;
							}
							break;

						default:
							if (fieldEdge?.node?.__typename) {
								fireErrorAnalyticsOncePerGraphQLType(
									fieldEdge?.node?.__typename,
									createAnalyticsEvent,
								);
							}
							// Unrecognised field type encountered. Render empty cell.
							break;
					}
				});

				if (fieldElements.length) {
					result.set(fieldSet.fieldSetId, {
						cellElement: fieldElements,
						spacing,
						multipleFieldLayoutAdjustment,
						...(fieldId ? { fieldId } : {}),
					});
				}
			});

		// Always include the Issue Operations meatball menu cell.
		// But show it empty if isIssueOperationMeatballMenuEnabled is false
		result.set(COLUMN_ID_MEATBALL_MENU, {
			cellElement: isIssueOperationMeatballMenuEnabled && (
				<IssueOperations
					key={COLUMN_ID_MEATBALL_MENU}
					workType={issueData?.issueTypeField?.issueType?.name}
					issueId={Number(issueData.issueId)}
					issueKey={issueData.key}
					onIssueRefetch={tableInjectedProps.onIssueRefetch}
					onIssueDelete={tableInjectedProps.onIssueDelete}
					isCreateSubTaskAllowed={isCreateSubTaskAllowed}
				/>
			),
		});

		result.set(COLUMN_ID_CHECKBOX, {
			cellElement: (
				<CheckboxCell
					issueId={issueData.issueId}
					rowIndex={rowIndex}
					{...(expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false) && { depth })}
				/>
			),
		});

		return result;
	}, [
		isIssueHierarchyEnabled,
		issueEdgeData,
		isMergedColumnEnabled,
		fieldSetsData,
		isIssueOperationMeatballMenuEnabled,
		issueData,
		tableInjectedProps.onIssueRefetch,
		tableInjectedProps.onIssueDelete,
		tableInjectedProps.onIssueLinkSelected,
		tableInjectedProps.issueSearchBaseUrl,
		isCreateSubTaskAllowed,
		rowIndex,
		isRowHoveredOrSelected,
		itemsConnectionId,
		depth,
		triggerProps,
		isFormVisible,
		groupId,
		columnConfigByIdMap,
		isTimelineEnabled,
		isInlineEditingExtendedFieldSupportEnabled,
		handleUpdateCache,
		createAnalyticsEvent,
		columns,
		isMergedCellFocused,
		groupConnectionId,
	]);

	const cellBorderWithNoInlineBordersStyles = useMemo(() => {
		if (cellBorder === undefined) {
			return noInlineBordersStyles;
		}
		return [...castArray(cellBorder), noInlineBordersStyles];
	}, [cellBorder]);

	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
	const isConditionalFormattingEnabled = useIsConditionalFormattingEnabled();
	let scrollLeft = 0;
	let hasScrolledHorizontally = false;

	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('issue_table_frozen_columns') && !fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		scrollLeft = useScrollStateSelector((scrollState) => scrollState.scrollLeft) || 0;
	}

	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('issue_table_frozen_columns') && fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		hasScrolledHorizontally = useScrollStateSelector((scrollState) => scrollState.scrollLeft > 0);
	}

	return (
		<>
			{columns.map(
				(
					{
						id,
						type,
						isPlaceHolderField,
						isColumnSticky,
						stickyOffsets,
						isLastStickyColumn,
						title,
					},
					index,
				) => {
					const field = fieldElementByIdMap.get(id);
					const columnIds = field?.columnIds; /* when cells are merged, multiple column ids */

					const formatting = (() => {
						if (!fg('jira_list_conditional_formatting')) return undefined;
						if (!isConditionalFormattingEnabled) return undefined;
						/* merged cell is 1 column, many fields. check each columnId for the column's rules -> find first rule */
						if (columnIds) {
							const rules = columnIds.map((columnId) => colorForId[columnId]).filter(Boolean);
							if (!rules.length) return undefined;
							const appliedRuleIndex = Math.min(...rules.map(({ index: i }) => i));
							/* find rule with lowest index */
							return rules.find(({ index: i }) => appliedRuleIndex === i);
						}
						return colorForId[id];
					})();

					return (
						<Fragment key={id}>
							{
								// TODO: EM-7165 – Update the extra filler cell logic when refactoring the column picker column
								// Empty cell to fill remainig space if configured columns are not wide enough to fill the table
								isFixedTableLayoutEnabled && id === COLUMN_ID_MEATBALL_MENU && (
									<TableCell
										border={cellBorderWithNoInlineBordersStyles}
										aria-hidden
										{...(isConditionalFormattingEnabled && {
											formatting: formattingStyles,
											// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
											style: {
												'--issue-row-bg': formatting?.value,
												'--issue-row-hover-bg': formatting?.hoverValue,
											} as CSSProperties,
										})}
									/>
								)
							}
							{isPlaceHolderField && fg('jira_inline_field_config_gate') ? (
								<LoadingCell
									cellBorder={cellBorder}
									{...(isStickyColumnsVisible &&
										fg('issue_table_frozen_columns') && {
											isColumnSticky,
											stickyOffsets,
											hasRightShadow: fg('jira_list_frozen_column_bugfix')
												? isLastStickyColumn && hasScrolledHorizontally
												: isLastStickyColumn && scrollLeft > 0,
										})}
								/>
							) : (
								<IssueRowCell
									type={type}
									{...(fg('nin_duplicated_fields_info_a11y_fix') && {
										issueKey: issueData?.key,
										fieldName: title,
									})}
									beforeElement={index === 0 ? beforeFirstCell : undefined}
									cellElement={field?.cellElement}
									spacing={field?.spacing}
									multipleFieldLayoutAdjustment={field?.multipleFieldLayoutAdjustment}
									border={cellBorder}
									{...(isConditionalFormattingEnabled && {
										formatting: fg('issue_table_frozen_columns')
											? formattingStyles
											: formattingStylesOld,
										// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
										style: {
											'--issue-row-bg': formatting?.value,
											'--issue-row-hover-bg': formatting?.hoverValue,
										} as CSSProperties,
									})}
									{...(isStickyColumnsVisible &&
										fg('issue_table_frozen_columns') && {
											isColumnSticky,
											stickyOffsets,
											hasRightShadow: fg('jira_list_frozen_column_bugfix')
												? isLastStickyColumn && hasScrolledHorizontally
												: isLastStickyColumn && scrollLeft > 0,
											isColumnResizing:
												isColumnSticky &&
												(resizingColumnIndex ?? 0) < index &&
												fg('issue_table_frozen_columns'),
										})}
									{...(field?.fieldId && { fieldId: field.fieldId })}
									{...(id === COLUMN_ID_MERGED_COLUMN &&
										fg('jira_list_merged_cell_action_buttons_visibility') && {
											onFocusHandler,
											onBlurHandler,
										})}
								/>
							)}
						</Fragment>
					);
				},
			)}
		</>
	);
};

const noSpacingStyles = xcss({
	padding: 'space.0',
});

const noInlineBordersStyles = xcss({
	borderInlineStartWidth: '0',

	borderInlineEndWidth: '0',
});

const formattingStyles = xcss({
	// @ts-expect-error XCSS typings don't allow css variables
	backgroundColor: 'var(--issue-row-bg, inherit)',
	':hover': {
		// @ts-expect-error XCSS typings don't allow css variables
		backgroundColor: 'var(--issue-row-hover-bg, inherit)',
	},
});

const formattingStylesOld = xcss({
	// @ts-expect-error XCSS typings don't allow css variables
	backgroundColor: 'var(--issue-row-bg)',
	':hover': {
		// @ts-expect-error XCSS typings don't allow css variables
		backgroundColor: 'var(--issue-row-hover-bg)',
	},
});

const typeCellStyles = xcss({
	paddingInlineStart: 'space.050',
});

const typeCellNoHierarchyStyles = xcss({
	paddingInlineStart: 'space.100',
});

const childCreateButtonStyle = xcss({
	marginInlineStart: 'auto',

	marginInlineEnd: 'space.100',
});

export const IssueRow: React.MemoExoticComponent<(props: Props) => React.JSX.Element> =
	memo(IssueRowWithFragments);
